import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Breadcrumb from '../../../../components/bootstrap/Breadcrumb';
import Button from '../../../../components/bootstrap/Button';
import Card, { CardBody, CardHeader, CardLabel, CardTitle } from '../../../../components/bootstrap/Card';
import showNotification from '../../../../components/extras/showNotification';
import SelectEstablishment from '../../../../components/MyCustom/SelectEstablishment';
import TableCustom from '../../../../components/MyCustom/TableCustom';
import ModalRegisterQuestions from '../../../../components/modals/Questions/ModalRegister';
import notificationBody from '../../../../helpers/utils/functions/notificationBody';
import useDarkMode from '../../../../hooks/useDarkMode';
import { DefaultContext } from '../../../../contexts/default';
import Page from '../../../../layout/Page/Page';
import PageWrapper from '../../../../layout/PageWrapper/PageWrapper';
import SubHeader, { SubHeaderLeft, SubHeaderRight } from '../../../../layout/SubHeader/SubHeader';
import { demoPages } from '../../../../menu';
import api from '../../../../services/api';

const Questions = () => {
  const { estabSelected, user } = useContext(DefaultContext);
  const { darkModeStatus } = useDarkMode();

  const [loading, setLoading] = useState(false);
  const [openModalRegister, setOpenModalRegister] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [questionSelected, setQuestionSelected] = useState(null);

  const getApiMessage = useCallback(
    (error) =>
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      error?.code ||
      'Falha ao consultar questionamentos',
    [],
  );

  const handleOpenModalRegister = useCallback(() => {
    setQuestionSelected(null);
    setOpenModalRegister(true);
  }, []);

  const handleCloseModalRegister = useCallback(() => {
    setQuestionSelected(null);
    setOpenModalRegister(false);
  }, []);

  const createQuestion = useCallback(
    (payload) => api.post('routes/evaluation/forms', payload),
    [],
  );

  const updateQuestion = useCallback(
    (formId, payload) =>
      api.put(`routes/evaluation/forms/${formId}`, payload, {
        params: { estabId: estabSelected },
      }),
    [estabSelected],
  );

  const getQuestionById = useCallback(
    async (questionId) => {
      const response = await api.get(`routes/evaluation/forms/${questionId}`, {
        params: { estabId: estabSelected },
      });

      return response?.data;
    },
    [estabSelected],
  );

  const handleOpenModalEdit = useCallback(async (item) => {
    try {
      const detail = item?.questions ? item : await getQuestionById(item.id);

      setQuestionSelected({
        ...item,
        ...detail,
      });
      setOpenModalRegister(true);
    } catch (error) {
      showNotification('', notificationBody(getApiMessage(error)), 'danger');
    }
  }, [getApiMessage, getQuestionById]);

  const loadQuestions = useCallback(async () => {
    if (!estabSelected) {
      setQuestions([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await api.get('routes/evaluation/forms/active', {
        params: { estabId: estabSelected },
      });

      const listData = (response?.data || []).map((item) => ({
        ...item,
        question_count: item.question_count ?? item.questions?.length ?? 0,
      }));

      const orderedData = [...listData].sort((a, b) =>
        (a.name || '').localeCompare(b.name || ''),
      );
      setQuestions(orderedData);
    } catch (error) {
      showNotification('', notificationBody(getApiMessage(error)), 'danger');
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  }, [estabSelected, getApiMessage]);

  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  const columns = useMemo(
    () => [
      {
        label: 'Nome',
        field: 'name',
      },
      {
        label: 'Versão',
        field: 'version',
      },
      {
        label: 'Total de questões',
        field: 'question_count',
      },
      {
        label: 'Status',
        field: 'active',
        format: (row) => (
          <Button
            isLink
            color={row.active ? 'success' : 'danger'}
            icon='Circle'
            className='text-nowrap'
          >
            {row.active ? 'Ativo' : 'Inativo'}
          </Button>
        ),
        formatExport: (value) => (value ? 'Ativo' : 'Inativo'),
      },
      {
        label: 'Ações',
        field: 'actions',
        format: (row) => (
          <Button color='dark' icon='Edit' size='sm' onClick={() => handleOpenModalEdit(row)} />
        ),
        noExport: true,
      },
    ],
    [handleOpenModalEdit],
  );

  return (
    <PageWrapper title={demoPages.pageLayout.subMenu.questions.text}>
      <SubHeader>
        <SubHeaderLeft>
          <Breadcrumb
            list={[
              { title: 'Cadastros', to: '/registers/questions' },
              {
                title: 'Questionamentos',
                to: '/registers/questions',
              },
            ]}
          />
        </SubHeaderLeft>
        <SubHeaderRight>
          <Button
            style={{ color: 'black' }}
            color='warning'
            icon='plus'
            shadow='sm'
            hoverShadow='sm'
            isDisable={!estabSelected}
            onClick={handleOpenModalRegister}
          >
            Cadastrar
          </Button>
        </SubHeaderRight>
      </SubHeader>
      <Page container='fluid'>
        <Card>
          <CardHeader>
            <CardLabel icon='QuestionAnswer' iconColor={darkModeStatus ? 'light' : 'dark'}>
              <CardTitle>Questionamentos</CardTitle>
            </CardLabel>
          </CardHeader>
          <CardBody className='table-responsive p-5'>
            <div className='row'>
              {
                user.role === "admin"
                  ?
                  <SelectEstablishment />
                  :
                  <></>
              }
            </div>
            {loading ? (
              <></>
            ) : !estabSelected ? (
              <div className='text-center py-5 text-muted'>
                Selecione um estabelecimento para visualizar os questionamentos
              </div>
            ) : questions.length === 0 ? (
              <div className='text-center py-5 text-muted'>Nenhum questionamento encontrado</div>
            ) : (
              <TableCustom
                titlePdf='Questionamentos'
                fileName='Questionamentos'
                icon='Comment'
                fontSize={8}
                columns={columns}
                rows={questions}
              />
            )}
          </CardBody>
        </Card>
      </Page>
      <ModalRegisterQuestions
        open={openModalRegister}
        setIsOpen={setOpenModalRegister}
        setIsClose={handleCloseModalRegister}
        estabId={estabSelected}
        questionSelected={questionSelected}
        onSubmit={createQuestion}
        onUpdate={updateQuestion}
        onSuccess={loadQuestions}
      />
    </PageWrapper>
  );
};

export default Questions;
